import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUser, clearAuth } from '../../utils/auth';
import NotificationBell from '../../components/NotificationBell';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ThemeToggle from '../../components/ThemeToggle';
import {
  Home,
  Navigation,
  Calendar,
  MessageSquare,
  User,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Heart,
  FileText,
  CreditCard,
  Settings,
  Stethoscope,
  History,
  ShieldCheck,
  Inbox
} from 'lucide-react';

// Sidebar menu items
const sidebarItems = [
  { icon: Home, label: 'Dashboard', path: '/patient/dashboard', section: 'dashboard' },
  { icon: Navigation, label: 'My Journey', path: '/patient/dashboard', section: 'journey' },
  { icon: Inbox, label: 'My Requests', path: '/patient/dashboard', section: 'requests' },
  { icon: Calendar, label: 'Appointments', path: '/patient/dashboard', section: 'appointments' },
  { icon: Stethoscope, label: 'My Treatments', path: '/patient/dashboard', section: 'treatments' },
  { icon: FileText, label: 'Medical Records', path: '/patient/dashboard', section: 'records' },
  { icon: MessageSquare, label: 'Messages', path: '/patient/dashboard', section: 'messages' },
  { icon: CreditCard, label: 'Payments', path: '/patient/dashboard', section: 'payments' },
  { icon: History, label: 'History', path: '/patient/dashboard', section: 'history' },
  { icon: Settings, label: 'Settings', path: '/patient/dashboard', section: 'settings' },
  { icon: ShieldCheck, label: 'AI & Privacy', path: '/patient/dashboard', section: 'ai-privacy' },
];

export default function PatientLayout({ children, activeSection, setActiveSection }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change for mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, activeSection]);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const getInitials = (name) => {
    if (!name) return 'P';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="patient-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
        transform transition-transform duration-300 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Heart className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <span className="font-heading font-bold text-slate-900 dark:text-white">MedRahi Patient</span>
              <p className="text-xs text-slate-500 dark:text-slate-400">Your Health Journey</p>
            </div>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Patient Profile Card */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14 ring-2 ring-emerald-500/20 ring-offset-2 ring-offset-white dark:ring-offset-slate-900">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 font-semibold text-lg">
                {getInitials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-slate-900 dark:text-white truncate">
                {user?.full_name}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Patient ID: {user?.patient_id || 'MR-' + (user?.id || '0000').toString().slice(-4)}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 h-[calc(100vh-280px)]">
          <nav className="p-4 space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.section;
              
              return (
                <button
                  key={item.section}
                  onClick={() => setActiveSection(item.section)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-left
                    transition-all duration-200
                    ${isActive 
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600 dark:text-emerald-400' : ''}`} strokeWidth={1.5} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="lg:pl-72">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
          <div className="h-16 px-4 lg:px-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5 text-slate-600 dark:text-slate-400" strokeWidth={1.5} />
              </button>
              
              <div>
                <h1 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">
                  {sidebarItems.find(i => i.section === activeSection)?.label || 'Dashboard'}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 hidden sm:block">
                  Welcome back, {user?.full_name?.split(' ')[0]}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ThemeToggle />
              
              <NotificationBell role="patient" />

              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-sm font-medium">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-heading">{user?.full_name}</span>
                      <span className="text-xs font-normal text-slate-500">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setActiveSection('settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveSection('records')}>
                    <FileText className="w-4 h-4 mr-2" />
                    Medical Records
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 pb-24 lg:pb-8 min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation - Custom for Patient (section-based) */}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 lg:hidden safe-area-bottom"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-center justify-around h-16 px-2">
          {[
            { icon: Home, label: 'Home', section: 'dashboard' },
            { icon: Navigation, label: 'Journey', section: 'journey' },
            { icon: Calendar, label: 'Appointments', section: 'appointments' },
            { icon: MessageSquare, label: 'Messages', section: 'messages' },
            { icon: User, label: 'Profile', section: 'settings' },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.section;
            return (
              <button
                key={item.section}
                onClick={() => setActiveSection(item.section)}
                className={`flex flex-col items-center justify-center flex-1 h-full px-1 py-2 transition-all duration-200 ${
                  isActive ? 'bg-emerald-50 dark:bg-emerald-900/30' : ''
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <div className={`p-1.5 rounded-xl transition-all duration-200 ${
                  isActive ? 'bg-emerald-50 dark:bg-emerald-900/30' : ''
                }`}>
                  <Icon 
                    className={`w-5 h-5 transition-colors ${
                      isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'
                    }`}
                    strokeWidth={isActive ? 2 : 1.5}
                  />
                </div>
                <span className={`text-[10px] font-medium mt-0.5 transition-colors ${
                  isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'
                }`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
